import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  CAlert,
  CBadge,
  CButton,
  CCard,
  CCardBody,
  CCardText,
  CCol,
  CContainer,
  CListGroup,
  CListGroupItem,
  CRow,
  CSpinner,
} from '@coreui/react'
import {
  cilArrowCircleLeft,
  cilChevronBottom,
  cilChevronTop,
  cilEnvelopeClosed,
  cilHandshake,
  cilPhone,
  cilSave,
  cilPlus,
  cilChevronRight,
  cilXCircle,
  cilLightbulb,
  cilFactory,
} from '@coreui/icons'
import CIcon from '@coreui/icons-react'
import apiClient from '../../services/apiClient'
import { useNavigate } from 'react-router-dom'
import { buildApiUrl } from '../../utils/getConfiguration'
import PartnerInfoRow from '../../components/PartnerInfoRow'
import { useTranslation } from 'react-i18next'
import { SplashScreen } from '@capacitor/splash-screen'
import { normalizeLanguage } from '../../utils/i18nConfig'
import {
  sanitizeValue,
  normalizeUrl,
  handlePhoneClick,
  handleMailClick,
  readSupplierAssignment,
  writeSupplierAssignment,
  clearSupplierAssignment,
  filterManualSuppliers,
  filterPreferredSuppliers,
} from '../../utils/supplierHelpers'
import InternalAppHeader from './components/InternalAppHeader'

const SuppliersInno = () => {
  const [preferredPartners, setPreferredPartners] = useState([])
  const [startups, setStartups] = useState([])
  const [manualPartners, setManualPartners] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [showPreferredAlert, setShowPreferredAlert] = useState(false)
  const [showSteps, setShowSteps] = useState(false)
  const [isMutating, setIsMutating] = useState(false)
  const [expandedPreferred, setExpandedPreferred] = useState({})

  const { t, i18n } = useTranslation()

  const navigate = useNavigate()

  const hideSplashScreen = useCallback(() => {
    if (typeof window === 'undefined') {
      return
    }

    const splash = document.getElementById('splash-screen')
    if (splash) {
      splash.style.display = 'none'
    }

    if (typeof SplashScreen?.hide === 'function') {
      SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) => {
        console.warn('Unable to hide splash screen in SuppliersInno', error)
      })
    } else if (typeof window.SplashScreen?.hide === 'function') {
      window.SplashScreen.hide({ fadeOutDuration: 200 }).catch((error) => {
        console.warn('Unable to hide splash screen in SuppliersInno', error)
      })
    }
  }, [])

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    const storedLanguage = window.localStorage.getItem('i18nextLng')
    if (storedLanguage) {
      i18n.changeLanguage(normalizeLanguage(storedLanguage))
    }
  }, [i18n])

  const accountContext = useMemo(() => {
    if (typeof window === 'undefined') {
      return null
    }
    const account_ID = window.localStorage.getItem('account_ID')
    const reminder_ID = window.localStorage.getItem('taskid')
    const grp_ID = window.localStorage.getItem('grp_ID')
    return { account_ID, reminder_ID, grp_ID }
  }, [])

  const refreshVisibility = useCallback(() => {
    const { supplierId, supplierName, supplierPhone } = readSupplierAssignment()
    const hasAny = [supplierId, supplierName, supplierPhone].some((value) => sanitizeValue(value))
    setShowPreferredAlert(!hasAny)
  }, [])

  const fetchData = useCallback(
    async (signal) => {
      if (!accountContext?.grp_ID) {
        return
      }
      setLoading(true)
      setError(null)
      try {
        const { account_ID, reminder_ID, grp_ID } = accountContext
        const requestConfig = signal ? { signal } : {}

        const paramsBase = { account_ID, reminder_ID, grp_ID }

        const [startupsRes, preferredRes, manualRes] = await Promise.all([
          apiClient.get(buildApiUrl('startups', { grp_ID }), requestConfig),
          apiClient.get(buildApiUrl('supplierPref', paramsBase), requestConfig),
          apiClient.get(buildApiUrl('supplierPrefManual', paramsBase), requestConfig),
        ])

        const startupsData = Array.isArray(startupsRes.data) ? startupsRes.data : []
        const preferredData = Array.isArray(preferredRes.data) ? preferredRes.data : []
        const manualData = Array.isArray(manualRes.data) ? manualRes.data : []

        setStartups(startupsData)
        setPreferredPartners(preferredData)
        setManualPartners(manualData)
        refreshVisibility()
      } catch (fetchError) {
        if (apiClient.isCancel?.(fetchError) || fetchError?.name === 'CanceledError') {
          return
        }
        console.error('Error fetching innovation partners', fetchError)
        setError('suppliersInno.messages.fetchError')
      } finally {
        setLoading(false)
      }
    },
    [accountContext, refreshVisibility],
  )

  useEffect(() => {
    hideSplashScreen()
  }, [hideSplashScreen])

  useEffect(() => {
    if (!accountContext?.grp_ID) {
      return undefined
    }
    const controller = new AbortController()
    fetchData(controller.signal).finally(hideSplashScreen)
    return () => controller.abort()
  }, [accountContext, fetchData, hideSplashScreen])

  const truncateText = useCallback((text, limit = 120) => {
    const sanitized = sanitizeValue(text)
    if (!sanitized) {
      return ''
    }
    return sanitized.length > limit ? `${sanitized.substring(0, limit)}…` : sanitized
  }, [])

  const togglePreferredDescription = useCallback((partnerKey) => {
    setExpandedPreferred((prev) => ({
      ...prev,
      [partnerKey]: !prev[partnerKey],
    }))
  }, [])

  const handleClickAssign = useCallback(
    async (startupId, type) => {
      if (isMutating) {
        return
      }
      try {
        setIsMutating(true)
        const { account_ID } = accountContext || {}
        if (!account_ID || !startupId) {
          return
        }
        await apiClient.get(
          buildApiUrl('startupAssign', { account_ID, startup_ID: startupId, type }),
        )

        const assignedStartup = startups.find((item) => (item.id ?? item.startup_id) === startupId)
        const startupName =
          sanitizeValue(
            assignedStartup?.idea ?? assignedStartup?.name ?? assignedStartup?.company,
          ) || t('suppliersInno.labels.startupFallback', { id: startupId })
        const startupPhone = sanitizeValue(
          assignedStartup?.supplierPhone ?? assignedStartup?.contactPhone ?? assignedStartup?.phone,
        )

        writeSupplierAssignment({
          supplierId: String(startupId),
          supplierName: startupName,
          supplierPhone: startupPhone,
        })

        refreshVisibility()
        await fetchData()
      } catch (error) {
        console.error('Error assigning startup', error)
      } finally {
        setIsMutating(false)
      }
    },
    [accountContext, fetchData, isMutating, refreshVisibility, startups],
  )

  const handleClickUnassign = useCallback(async () => {
    if (isMutating) {
      return
    }
    try {
      setIsMutating(true)
      const { account_ID, reminder_ID } = accountContext || {}
      if (!account_ID || !reminder_ID) {
        return
      }
      await apiClient.get(
        buildApiUrl('supplierUnassign', {
          account_ID,
          reminder_ID,
        }),
      )
      clearSupplierAssignment()
      refreshVisibility()
      await fetchData()
    } catch (error) {
      console.error('Error unassigning partner', error)
    } finally {
      setIsMutating(false)
    }
  }, [accountContext, fetchData, isMutating, refreshVisibility])

  const handleClickUnassignManual = useCallback(async () => {
    if (isMutating) {
      return
    }
    try {
      setIsMutating(true)
      const { account_ID, reminder_ID } = accountContext || {}
      if (!account_ID || !reminder_ID) {
        return
      }
      await apiClient.get(
        buildApiUrl('supplierUnassignManual', {
          account_ID,
          reminder_ID,
        }),
      )
      clearSupplierAssignment()
      refreshVisibility()
      await fetchData()
    } catch (error) {
      console.error('Error unassigning manual partner', error)
    } finally {
      setIsMutating(false)
    }
  }, [accountContext, fetchData, isMutating, refreshVisibility])

  const toggleSteps = useCallback(() => {
    setShowSteps((prev) => !prev)
  }, [])

  const typeLabelMap = useMemo(
    () => ({
      F: t('suppliersInno.types.F'),
      C: t('suppliersInno.types.C'),
      J: t('suppliersInno.types.J'),
    }),
    [t],
  )

  const getTypeLabel = useCallback((type) => typeLabelMap[type] || null, [typeLabelMap])

  const manualSavedPartners = useMemo(() => filterManualSuppliers(manualPartners), [manualPartners])

  const listedPreferredPartners = useMemo(
    () => filterPreferredSuppliers(preferredPartners),
    [preferredPartners],
  )

  const manualCount = manualSavedPartners.length
  const preferredCount = listedPreferredPartners.length
  const startupCount = startups.length

  return (
    <div className="buying-list-wrapper min-vh-100 d-flex flex-column">
      <CContainer className="py-4 flex-grow-1">
        <InternalAppHeader
          title={t('suppliersInno.headerTitle')}
          onBack={() => window.history.back()}
        />
        <CRow className="justify-content-center g-0 mt-2">
          <CCol lg={10}>
            <CCard className="mb-0 border-0 shadow-sm">
              <CCardBody>
                <div className="d-flex justify-content-end align-items-center mb-0">
                  <CButton
                    color="success"
                    className="suppliers-inno__next-btn"
                    onClick={() => {
                      console.log('Selected startup list')
                      navigate('/suppliersinnolist')
                    }}
                    aria-label={t('suppliersInno.actions.openSaved')}
                  >
                    <CIcon icon={cilChevronRight} />
                  </CButton>
                </div>
                {showPreferredAlert && !loading && manualCount === 0 && preferredCount === 0 && (
                  <div className="reminder-area-card__item mb-4">
                    <div className="reminder-area-card__empty">
                      <span
                        className="reminder-area-card__empty-icon text-primary"
                        aria-hidden="true"
                      >
                        <CIcon icon={cilFactory} size="lg" />
                      </span>
                      <div>
                        <span className="reminder-area-card__empty-title">
                          {t('suppliersInno.messages.emptyPreferredTitle')}
                        </span>
                        <span className="reminder-area-card__empty-caption">
                          {t('suppliersInno.messages.emptyPreferredCaption')}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {(manualCount > 0 || showPreferredAlert) && (
                  <CRow className="align-items-center mb-3 g-2">
                    <CCol xs="auto">
                      <h4 className="mb-0">{t('suppliersInno.sections.customPartners')}</h4>
                    </CCol>
                    <CCol xs="auto" className="d-flex gap-2 align-items-center flex-wrap">
                      <CBadge color={manualCount === 0 ? 'warning' : 'success'}>
                        {t('suppliersInno.badges.manualCount', { count: manualCount })}
                      </CBadge>
                    </CCol>
                    <CCol xs="auto">
                      <CButton
                        color="primary"
                        size="sm"
                        aria-label={t('suppliersInno.actions.setPreferred')}
                        onClick={() => navigate('/suppliersselection')}
                      >
                        <CIcon icon={cilPlus} />
                      </CButton>
                    </CCol>
                  </CRow>
                )}

                {manualCount > 0 && (
                  <>
                    <CListGroup className="mb-4">
                      {manualSavedPartners.map((partner) => {
                        const name =
                          sanitizeValue(partner.supplier_name) ||
                          t('suppliersInno.labels.manualPartner')
                        const contactName = sanitizeValue(partner.supplier_contact_name)
                        const contactTel = sanitizeValue(partner.supplier_contact_tel)
                        const contactMail = sanitizeValue(partner.supplier_contact_mail)
                        const picture = partner.pic
                          ? partner.pic
                          : partner.supplierPic
                            ? `https://displatform.com/assets/suppliers/${partner.supplierPic}`
                            : null
                        const infoLines = [
                          contactName,
                          contactTel
                            ? t('suppliersInno.labels.phone', { value: contactTel })
                            : null,
                          contactMail
                            ? t('suppliersInno.labels.email', { value: contactMail })
                            : null,
                        ].filter(Boolean)
                        return (
                          <CListGroupItem
                            key={partner.supplier_id ?? `${name}-${contactTel}-${contactMail}`}
                            className="innovation-partner-card manual-partner-item d-flex align-items-start gap-3 flex-wrap p-3 shadow-sm"
                          >
                            <PartnerInfoRow
                              icon={
                                <CIcon
                                  icon={cilFactory}
                                  size="lg"
                                  className="text-secondary flex-shrink-0"
                                  aria-hidden="true"
                                />
                              }
                              picture={picture}
                              title={name}
                              badge={
                                <CBadge color="secondary">
                                  {t('suppliersInno.badges.manualLabel')}
                                </CBadge>
                              }
                              lines={infoLines}
                              imageWidth={64}
                            />
                            <div className="d-flex gap-2 align-items-center flex-wrap ms-auto">
                              <CButton
                                color={contactTel ? 'primary' : 'secondary'}
                                disabled={!contactTel}
                                onClick={() => handlePhoneClick(contactTel)}
                                aria-label={t('suppliersInno.actions.call', { name })}
                              >
                                <CIcon icon={cilPhone} size="lg" />
                              </CButton>
                              <CButton
                                color={contactMail ? 'primary' : 'secondary'}
                                disabled={!contactMail}
                                onClick={() => handleMailClick(contactMail, name)}
                                aria-label={t('suppliersInno.actions.email', { name })}
                              >
                                <CIcon icon={cilEnvelopeClosed} size="lg" />
                              </CButton>
                              <CButton
                                color="danger"
                                variant="outline"
                                onClick={handleClickUnassignManual}
                                aria-label={t('suppliersInno.actions.unassign', { name })}
                                disabled={isMutating}
                              >
                                <CIcon icon={cilXCircle} size="lg" />
                              </CButton>
                            </div>
                          </CListGroupItem>
                        )
                      })}
                    </CListGroup>
                  </>
                )}

                {preferredCount > 0 && (
                  <>
                    <CRow className="align-items-center mb-3 g-2">
                      <CCol xs="auto">
                        <h4 className="mb-0">{t('suppliersInno.sections.preferredPartners')}</h4>
                      </CCol>
                      <CCol xs="auto">
                        <CBadge color="info">{preferredCount}</CBadge>
                      </CCol>
                    </CRow>
                    <CListGroup className="mb-4">
                      {listedPreferredPartners.map((partner) => {
                        const name =
                          sanitizeValue(partner.supplierName) ||
                          sanitizeValue(partner.supplier_name) ||
                          t('suppliersInno.labels.innovationPartner')
                        const description = sanitizeValue(partner.description)
                        const phone = sanitizeValue(partner.supplierPhone)
                        const mail = sanitizeValue(partner.supplierMail)
                        const picture = `https://displatform.com/assets/suppliers/${partner.supplierPic}`
                        const typeLabel = getTypeLabel(partner.type)
                        const infoLines = [
                          phone ? t('suppliersInno.labels.phone', { value: phone }) : null,
                          mail ? t('suppliersInno.labels.email', { value: mail }) : null,
                        ]
                        const partnerKey =
                          partner.supplier_id ??
                          `${name}-${phone ?? 'no-phone'}-${mail ?? 'no-mail'}`
                        const isDescriptionExpanded = !!expandedPreferred[partnerKey]
                        const descriptionActionLabel = isDescriptionExpanded
                          ? t('suppliersInno.labels.hideDescription')
                          : t('suppliersInno.labels.showDescription')
                        return (
                          <CListGroupItem
                            key={partnerKey}
                            className="preferred-partner-item innovation-partner-card d-flex align-items-start gap-3 flex-wrap shadow-sm p-3"
                          >
                            <div className="d-flex flex-column gap-2 flex-grow-1">
                              <PartnerInfoRow
                                icon={
                                  <CIcon
                                    icon={cilLightbulb}
                                    size="lg"
                                    className="text-warning flex-shrink-0"
                                    aria-hidden="true"
                                  />
                                }
                                picture={picture}
                                title={name}
                                badge={typeLabel ? <CBadge color="info">{typeLabel}</CBadge> : null}
                                lines={infoLines}
                                imageWidth={112}
                              />
                              {description && (
                                <div className="d-flex flex-column gap-2">
                                  <CButton
                                    color="link"
                                    className="p-0 d-inline-flex align-items-center gap-2"
                                    onClick={() => togglePreferredDescription(partnerKey)}
                                    aria-label={t('suppliersInno.labels.descriptionToggle', {
                                      action: descriptionActionLabel,
                                      name,
                                    })}
                                  >
                                    <span className="fw-semibold">{descriptionActionLabel}</span>
                                    <CIcon
                                      icon={
                                        isDescriptionExpanded ? cilChevronTop : cilChevronBottom
                                      }
                                    />
                                  </CButton>
                                  {isDescriptionExpanded && (
                                    <CCardText className="mb-0 text-medium-emphasis small">
                                      {description}
                                    </CCardText>
                                  )}
                                </div>
                              )}
                            </div>
                            <div className="d-flex gap-2 align-items-center flex-wrap ms-auto">
                              <CButton
                                color={phone ? 'primary' : 'secondary'}
                                disabled={!phone}
                                onClick={() => handlePhoneClick(phone)}
                                aria-label={t('suppliersInno.actions.call', { name })}
                              >
                                <CIcon icon={cilPhone} size="lg" />
                              </CButton>
                              <CButton
                                color={mail ? 'primary' : 'secondary'}
                                disabled={!mail}
                                onClick={() => handleMailClick(mail, name)}
                                aria-label={t('suppliersInno.actions.email', { name })}
                              >
                                <CIcon icon={cilEnvelopeClosed} size="lg" />
                              </CButton>
                              <CButton
                                color="danger"
                                variant="outline"
                                onClick={handleClickUnassign}
                                aria-label={t('suppliersInno.actions.unassign', { name })}
                                disabled={isMutating}
                              >
                                <CIcon icon={cilXCircle} size="lg" />
                              </CButton>
                            </div>
                          </CListGroupItem>
                        )
                      })}
                    </CListGroup>
                  </>
                )}

                <CCard className="mb-4" color="white">
                  <CCardBody>
                    <CRow className="g-3 align-items-center">
                      <CCol xs="auto">
                        <h5 className="mb-0">{t('suppliersInno.sections.innovationTutorial')}</h5>
                      </CCol>
                      <CCol xs="auto">
                        <CButton color="primary" variant="outline" size="sm" onClick={toggleSteps}>
                          <CIcon icon={showSteps ? cilChevronTop : cilChevronBottom} />
                        </CButton>
                      </CCol>
                    </CRow>
                    <CCardText className="mt-3 mb-0">
                      {showSteps
                        ? t('suppliersInno.tutorial.introExpanded')
                        : t('suppliersInno.tutorial.introCollapsed')}
                    </CCardText>
                    {showSteps && (
                      <CRow className="mt-3 g-3">
                        <CCol xs={12} md={6}>
                          <h6 className="fw-semibold">{t('suppliersInno.tutorial.step1Title')}</h6>
                          <CCardText className="mb-0">
                            {t('suppliersInno.tutorial.step1Text')}
                          </CCardText>
                        </CCol>
                        <CCol xs={12} md={6}>
                          <h6 className="fw-semibold">{t('suppliersInno.tutorial.step2Title')}</h6>
                          <CCardText className="mb-0">
                            {t('suppliersInno.tutorial.step2Text')}
                          </CCardText>
                        </CCol>
                        <CCol xs={12}>
                          <h6 className="fw-semibold">{t('suppliersInno.tutorial.step3Title')}</h6>
                          <CCardText className="mb-0">
                            {t('suppliersInno.tutorial.step3Text')}
                          </CCardText>
                        </CCol>
                        <CCol xs={12}>
                          <h6 className="fw-semibold">{t('suppliersInno.tutorial.step4Title')}</h6>
                          <CCardText className="mb-0">
                            {t('suppliersInno.tutorial.step4Text')}
                          </CCardText>
                        </CCol>
                      </CRow>
                    )}
                  </CCardBody>
                </CCard>

                <hr className="mt-4 mb-3" />
                <CRow className="align-items-center mb-3 g-2">
                  <CCol xs="auto">
                    <h4 className="mb-0">{t('suppliersInno.sections.selectService')}</h4>
                  </CCol>
                  <CCol xs="auto">
                    <CBadge color="primary">{startupCount}</CBadge>
                  </CCol>
                </CRow>

                {loading && (
                  <div className="d-flex align-items-center gap-2 mb-3">
                    <CSpinner size="sm" /> {t('suppliersInno.messages.loading')}
                  </div>
                )}
                {error && <CAlert color="danger">{t(error)}</CAlert>}
                {!loading && !error && startupCount === 0 && (
                  <CCardText className="alert alert-info">
                    {t('suppliersInno.messages.noPartners')}
                  </CCardText>
                )}
                {startupCount > 0 && (
                  <CListGroup>
                    {startups.map((startup) => {
                      const id = startup.id ?? startup.startup_id
                      const name =
                        sanitizeValue(startup.idea) ||
                        sanitizeValue(startup.name) ||
                        sanitizeValue(startup.company) ||
                        t('suppliersInno.labels.startupFallback', { id: id ?? '' })
                      const description = truncateText(startup.description)
                      const linkHref = normalizeUrl(startup.link)
                      const linkNode = linkHref ? (
                        <a href={linkHref} target="_blank" rel="noopener noreferrer">
                          {startup.link}
                        </a>
                      ) : null
                      const picture = startup.pic
                        ? `https://www.emerkur.eu/startups/${startup.pic}`
                        : null
                      const infoLines = [description, linkNode].filter(Boolean)
                      return (
                        <CListGroupItem
                          key={id ?? name}
                          className="innovation-partner-card d-flex align-items-start gap-3 flex-wrap p-3 shadow-sm"
                        >
                          <PartnerInfoRow
                            icon={
                              <CIcon
                                icon={cilLightbulb}
                                size="lg"
                                className="text-warning flex-shrink-0"
                                aria-hidden="true"
                              />
                            }
                            picture={picture}
                            title={name}
                            lines={infoLines}
                            imageWidth={112}
                          />
                          <div className="d-flex gap-2 align-items-center flex-wrap ms-auto">
                            <CButton
                              color="success"
                              variant="outline"
                              onClick={() => {
                                console.log('Save to favourites startup Nr.', id)
                                handleClickAssign(id, 'F')
                              }}
                              aria-label={t('suppliersInno.actions.saveFavourite', { name })}
                              disabled={isMutating}
                            >
                              <CIcon icon={cilChevronRight} size="lg" />
                            </CButton>
                            <CButton
                              color="info"
                              variant="outline"
                              onClick={() => {
                                console.log('Save for cooperation startup Nr.', id)
                                handleClickAssign(id, 'J')
                              }}
                              aria-label={t('suppliersInno.actions.saveCooperation', { name })}
                              disabled={isMutating}
                            >
                              <CIcon icon={cilHandshake} size="lg" />
                            </CButton>
                          </div>
                        </CListGroupItem>
                      )
                    })}
                  </CListGroup>
                )}
              </CCardBody>
            </CCard>
          </CCol>
        </CRow>
      </CContainer>
    </div>
  )
}

export default SuppliersInno
